/**
 * STATUS ENUMS -- Single Source of Truth
 *
 * Tất cả status enum values được định nghĩa tại đây.
 * Prisma schema, tRPC validation, frontend UI đều tham chiếu từ file này.
 *
 * Quy ước:
 * - Dùng `as const` object thay vì TypeScript enum để tương thích Zod + runtime
 * - Mỗi const object có type union tương ứng
 * - Đặt tên theo PascalCase + "Values" suffix cho object, không suffix cho type
 */

// ══════════════════════════════════════════════
// SYSTEM MODULE
// ══════════════════════════════════════════════

export const UserStatusValues = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  SUSPENDED: 'SUSPENDED',
} as const;
export type UserStatus = (typeof UserStatusValues)[keyof typeof UserStatusValues];

export const MachineStatusValues = {
  IDLE: 'IDLE',
  RUNNING: 'RUNNING',
  MAINTENANCE: 'MAINTENANCE',
  DOWN: 'DOWN',
} as const;
export type MachineStatus = (typeof MachineStatusValues)[keyof typeof MachineStatusValues];

// ══════════════════════════════════════════════
// SALES MODULE
// ══════════════════════════════════════════════

export const QuotationStatusValues = {
  DRAFT: 'DRAFT',
  SENT: 'SENT',
  PROCESSING: 'PROCESSING',
  ESTIMATED: 'ESTIMATED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const;
export type QuotationStatus = (typeof QuotationStatusValues)[keyof typeof QuotationStatusValues];

export const OrderStatusValues = {
  DRAFT: 'DRAFT',
  PENDING: 'PENDING',
  WAITING_PRE_PRESS: 'WAITING_PRE_PRESS',
  CONFIRMED: 'CONFIRMED',
  APPROVED: 'APPROVED',
  PLANNED: 'PLANNED',
  IN_PROGRESS: 'IN_PROGRESS',
  IN_PRODUCTION: 'IN_PRODUCTION',
  COMPLETED: 'COMPLETED',
  DELIVERED: 'DELIVERED',
  INVOICED: 'INVOICED',
  CLOSED: 'CLOSED',
  CANCELLED: 'CANCELLED',
} as const;
export type OrderStatus = (typeof OrderStatusValues)[keyof typeof OrderStatusValues];

// ══════════════════════════════════════════════
// PRODUCTION MODULE
// ══════════════════════════════════════════════

export const ProductionOrderStatusValues = {
  DRAFT: 'DRAFT',
  PRE_PRESS: 'PRE_PRESS',
  READY_FOR_SCHEDULING: 'READY_FOR_SCHEDULING',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  PRE_PRESS_COMPLETED: 'PRE_PRESS_COMPLETED',
  MATERIALS_READY: 'MATERIALS_READY',
  PLANNED: 'PLANNED',
  RELEASED: 'RELEASED',
  IN_PROGRESS: 'IN_PROGRESS',
  MANUFACTURED: 'MANUFACTURED',
  COMPLETED: 'COMPLETED',
  CLOSED: 'CLOSED',
  CANCELLED: 'CANCELLED',
  ON_HOLD: 'ON_HOLD',
  PAUSED: 'PAUSED',
} as const;
export type ProductionOrderStatus = (typeof ProductionOrderStatusValues)[keyof typeof ProductionOrderStatusValues];

export const RoutingStepStatusValues = {
  PLANNED: 'PLANNED',
  READY: 'READY',
  SCHEDULED: 'SCHEDULED',
  RUNNING: 'RUNNING',
  COMPLETED: 'COMPLETED',
  PAUSED: 'PAUSED',
  CANCELLED: 'CANCELLED',
} as const;
export type RoutingStepStatus = (typeof RoutingStepStatusValues)[keyof typeof RoutingStepStatusValues];

export const MaterialPlanningStatusValues = {
  PENDING: 'PENDING',
  ORDERED: 'ORDERED',
  PARTIAL_RECEIVED: 'PARTIAL_RECEIVED',
  RECEIVED: 'RECEIVED',
  CANCELLED: 'CANCELLED',
} as const;
export type MaterialPlanningStatus = (typeof MaterialPlanningStatusValues)[keyof typeof MaterialPlanningStatusValues];

export const MoldStatusValues = {
  NEW: 'NEW',
  REUSE: 'REUSE',
  REUSE_EDITED: 'REUSE_EDITED',
} as const;
export type MoldStatus = (typeof MoldStatusValues)[keyof typeof MoldStatusValues];

export const PrepressStatusValues = {
  PENDING: 'PENDING',
  LAYOUT_PROCESSING: 'LAYOUT_PROCESSING',
  PLATE_OUTPUT: 'PLATE_OUTPUT',
  MOLD_ORDERED: 'MOLD_ORDERED',
  COMPLETED: 'COMPLETED',
} as const;
export type PrepressStatus = (typeof PrepressStatusValues)[keyof typeof PrepressStatusValues];

// ══════════════════════════════════════════════
// QUALITY MODULE
// ══════════════════════════════════════════════

export const QCStatusValues = {
  PENDING: 'PENDING',
  INSPECTING: 'INSPECTING',
  PASSED: 'PASSED',
  WARNING: 'WARNING',
  FAILED: 'FAILED',
  REWORK: 'REWORK',
  RE_INSPECT: 'RE_INSPECT',
} as const;
export type QCStatus = (typeof QCStatusValues)[keyof typeof QCStatusValues];

export const NCRStatusValues = {
  OPEN: 'OPEN',
  INVESTIGATING: 'INVESTIGATING',
  CORRECTIVE_ACTION: 'CORRECTIVE_ACTION',
  VERIFICATION: 'VERIFICATION',
  PREVENTIVE_ACTION: 'PREVENTIVE_ACTION',
  CLOSED: 'CLOSED',
  CANCELLED: 'CANCELLED',
} as const;
export type NCRStatus = (typeof NCRStatusValues)[keyof typeof NCRStatusValues];

// ══════════════════════════════════════════════
// OUTSOURCING MODULE
// ══════════════════════════════════════════════

export const OutsourcePOStatusValues = {
  DRAFT: 'DRAFT',
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;
export type OutsourcePOStatus = (typeof OutsourcePOStatusValues)[keyof typeof OutsourcePOStatusValues];

export const PaymentStatusValues = {
  UNPAID: 'UNPAID',
  PARTIAL: 'PARTIAL',
  PAID: 'PAID',
} as const;
export type PaymentStatus = (typeof PaymentStatusValues)[keyof typeof PaymentStatusValues];

// ══════════════════════════════════════════════
// WAREHOUSE MODULE
// ══════════════════════════════════════════════

export const InventoryStatusValues = {
  AVAILABLE: 'AVAILABLE',
  RESERVED: 'RESERVED',
  LOW_STOCK: 'LOW_STOCK',
  OUT_OF_STOCK: 'OUT_OF_STOCK',
} as const;
export type InventoryStatus = (typeof InventoryStatusValues)[keyof typeof InventoryStatusValues];

export const DocumentStatusValues = {
  DRAFT: 'DRAFT',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;
export type DocumentStatus = (typeof DocumentStatusValues)[keyof typeof DocumentStatusValues];

// ══════════════════════════════════════════════
// SUPPLY MODULE
// ══════════════════════════════════════════════

export const PurchaseOrderStatusValues = {
  DRAFT: 'DRAFT',
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  ORDERED: 'ORDERED',
  PARTIAL_RECEIVED: 'PARTIAL_RECEIVED',
  RECEIVED: 'RECEIVED',
  CANCELLED: 'CANCELLED',
} as const;
export type PurchaseOrderStatus = (typeof PurchaseOrderStatusValues)[keyof typeof PurchaseOrderStatusValues];

export const PurchaseRequestStatusValues = {
  DRAFT: 'DRAFT',
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  ORDERED: 'ORDERED',
} as const;
export type PurchaseRequestStatus = (typeof PurchaseRequestStatusValues)[keyof typeof PurchaseRequestStatusValues];

// ══════════════════════════════════════════════
// LOGISTICS MODULE
// ══════════════════════════════════════════════

export const DeliveryStatusValues = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  IN_TRANSIT: 'IN_TRANSIT',
  DELIVERED: 'DELIVERED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;
export type DeliveryStatus = (typeof DeliveryStatusValues)[keyof typeof DeliveryStatusValues];

// ══════════════════════════════════════════════
// TECH MODULE
// ══════════════════════════════════════════════

export const DesignStatusValues = {
  PENDING: 'PENDING',
  DESIGNING: 'DESIGNING',
  REVIEWING: 'REVIEWING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const;
export type DesignStatus = (typeof DesignStatusValues)[keyof typeof DesignStatusValues];

export const ToolingStatusValues = {
  PENDING: 'PENDING',
  AVAILABLE: 'AVAILABLE',
  IN_USE: 'IN_USE',
  MAINTENANCE: 'MAINTENANCE',
  DAMAGED: 'DAMAGED',
  DISCARDED: 'DISCARDED',
  CANCELLED: 'CANCELLED',
} as const;
export type ToolingStatus = (typeof ToolingStatusValues)[keyof typeof ToolingStatusValues];

export const SpecStatusValues = {
  DRAFT: 'DRAFT',
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  OBSOLETE: 'OBSOLETE',
} as const;
export type SpecStatus = (typeof SpecStatusValues)[keyof typeof SpecStatusValues];

export const ReadinessStatusValues = {
  PENDING: 'PENDING',
  PARTIAL: 'PARTIAL',
  READY: 'READY',
} as const;
export type ReadinessStatus = (typeof ReadinessStatusValues)[keyof typeof ReadinessStatusValues];

// ══════════════════════════════════════════════
// FEEDBACK MODULE
// ══════════════════════════════════════════════

export const FeedbackStatusValues = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  RESOLVED: 'RESOLVED',
  CLOSED: 'CLOSED',
} as const;
export type FeedbackStatus = (typeof FeedbackStatusValues)[keyof typeof FeedbackStatusValues];

export const KpiStatusValues = {
  PENDING: 'PENDING',
  REVIEWED: 'REVIEWED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const;
export type KpiStatus = (typeof KpiStatusValues)[keyof typeof KpiStatusValues];

// ══════════════════════════════════════════════
// HELPER: Lấy mảng values từ const object
// ══════════════════════════════════════════════

/** Trả về mảng giá trị string từ Values object -- dùng cho z.enum() */
export function enumValues<T extends Record<string, string>>(obj: T): [T[keyof T], ...T[keyof T][]] {
  const vals = Object.values(obj) as T[keyof T][];
  return vals as [T[keyof T], ...T[keyof T][]];
}
